import React, { createContext, useState, useContext, useMemo, useEffect } from "react";
import characteristicsList from "../components/static/characteristics.json";
import { getLocalStorageEnumMap } from "../services/user/browserService";
import { listDatasets } from "../services/api/datasetService";
import { getVisibleCharacteristics } from "../services/api/tenantConfigService";

/**
 * SearchContext - Unified context for all search-related state
 *
 * Consolidates:
 * - DatasetContext (selectedDatasets - now multi-select array)
 * - ApplicationContext (enumMap, searchString, triggers)
 * - FilteredPeopleContext (search results, match source)
 * - SelectedCharacteristicsContext (available characteristics)
 * - TriggerContext (unused, removed)
 *
 * This reduces context overhead and prevents unnecessary re-renders
 * by keeping related state together.
 *
 * Datasets are fetched from DynamoDB (ACTIVE only) via API.
 */
export const SearchContext = createContext();

export const SearchProvider = ({ children }) => {
  // ==================== Dataset State ====================
  // Datasets fetched from API (ACTIVE only)
  const [datasets, setDatasets] = useState([]);
  const [datasetObjects, setDatasetObjects] = useState([]); // Full dataset objects with metadata
  const [datasetsLoading, setDatasetsLoading] = useState(true);
  const [datasetsError, setDatasetsError] = useState(null);

  // Selected datasets - now an array for multi-select
  const [selectedDatasets, setSelectedDatasets] = useState([]);

  // Fetch datasets from API on mount
  useEffect(() => {
    async function fetchDatasets() {
      try {
        setDatasetsLoading(true);
        const result = await listDatasets({ includeInactive: false });

        // Store full dataset objects (includes metadata)
        setDatasetObjects(result);

        // Extract dataset IDs for selection
        const datasetIds = result.map(ds => ds.dataset_id);
        setDatasets(datasetIds);

        // Default: select all active datasets (minimum 1 required)
        if (datasetIds.length > 0) {
          setSelectedDatasets(datasetIds);
        }
        setDatasetsError(null);
      } catch (error) {
        console.error('Failed to fetch datasets:', error);
        setDatasetsError(error.message);
        setDatasets([]);
        setDatasetObjects([]);
        // Don't clear selectedDatasets on error - keep previous selection
      } finally {
        setDatasetsLoading(false);
      }
    }

    fetchDatasets();
  }, []);

  // ==================== Search State (from ApplicationContext) ====================
  // Initialize enumMap from localStorage (excluding dataset - now handled separately)
  const [enumMap, setEnumMap] = useState(() => {
    const characteristicsMap = getLocalStorageEnumMap();
    characteristicsMap.delete("Dataset"); // Remove old single-dataset storage
    return characteristicsMap;
  });

  const [defaultMap] = useState(() => {
    const characteristicsMap = getLocalStorageEnumMap();
    characteristicsMap.delete("Dataset");
    return characteristicsMap;
  });

  const [searchString, setSearchString] = useState();
  const [triggerSearch, setTriggerSearch] = useState(null);
  const [triggerAttributeSearch, setTriggerAttributeSearch] = useState(null);

  // ==================== Search Results (from FilteredPeopleContext) ====================
  const [filteredPeople, setFilteredPeople] = useState([]);
  const [matchSource, setMatchSource] = useState('manual_filter'); // 'manual_filter' or 'face_match'
  const [matchAccuracy, setMatchAccuracy] = useState(null); // Match accuracy for face_match results
  const [capturedImageUrl, setCapturedImageUrl] = useState(null); // Captured image URL for display
  const [capturedImageS3Key, setCapturedImageS3Key] = useState(null); // S3 key of captured image

  // ==================== Characteristics (from SelectedCharacteristicsContext) ====================
  const [selectedCharacteristics, setSelectedCharacteristics] = useState(characteristicsList);

  // Visible characteristics - loaded from tenant config in DynamoDB
  // Default: all characteristics are visible
  const [visibleCharacteristics, setVisibleCharacteristics] = useState(() => {
    // Default: all characteristics visible
    return characteristicsList.reduce((acc, char) => {
      acc[char] = true;
      return acc;
    }, {});
  });

  // Load visible characteristics from tenant config on mount
  useEffect(() => {
    async function fetchVisibleCharacteristics() {
      try {
        const config = await getVisibleCharacteristics();
        // Only update if we got a non-empty config
        if (config && Object.keys(config).length > 0) {
          setVisibleCharacteristics(config);
        }
      } catch (error) {
        console.error('Failed to load visible characteristics from tenant config:', error);
        // Keep defaults on error
      }
    }

    fetchVisibleCharacteristics();
  }, []);

  // ==================== Context Value ====================
  // Memoize value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    // Dataset state
    datasets,
    datasetObjects,
    datasetsLoading,
    datasetsError,
    selectedDatasets,
    setSelectedDatasets,

    // Search/filter state
    enumMap,
    setEnumMap,
    defaultMap,
    searchString,
    setSearchString,
    triggerSearch,
    setTriggerSearch,
    triggerAttributeSearch,
    setTriggerAttributeSearch,

    // Search results
    filteredPeople,
    setFilteredPeople,
    matchSource,
    setMatchSource,
    matchAccuracy,
    setMatchAccuracy,
    capturedImageUrl,
    setCapturedImageUrl,
    capturedImageS3Key,
    setCapturedImageS3Key,

    // Characteristics
    selectedCharacteristics,
    setSelectedCharacteristics,
    visibleCharacteristics,
    setVisibleCharacteristics,
  }), [
    datasets,
    datasetObjects,
    datasetsLoading,
    datasetsError,
    selectedDatasets,
    enumMap,
    defaultMap,
    searchString,
    triggerSearch,
    triggerAttributeSearch,
    filteredPeople,
    matchSource,
    matchAccuracy,
    capturedImageUrl,
    capturedImageS3Key,
    selectedCharacteristics,
    visibleCharacteristics
  ]);

  return (
    <SearchContext.Provider value={value}>
      {children}
    </SearchContext.Provider>
  );
};

/**
 * Custom hook to use the SearchContext
 * @returns {Object} Search context value
 */
export const useSearch = () => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error('useSearch must be used within a SearchProvider');
  }
  return context;
};

// Backward compatibility exports (to be removed after migration)
export const DatasetContext = SearchContext;
export const DatasetProvider = SearchProvider;
export const ApplicationContext = SearchContext;
export const ApplicationProvider = SearchProvider;
export const FilteredPeopleContext = SearchContext;
export const FilteredPeopleProvider = SearchProvider;
export const SelectedCharacteristicsContext = SearchContext;
export const SelectedCharacteristicsProvider = SearchProvider;
